import { useCallback, type ReactNode } from 'react';

import { formatDate, formatTime } from '@/shared/format';
import { StatCard } from '@/shared/ui/stat-card';
import { dashboardApi } from '@/modules/dashboard/services/api';
import type { BacklogQueueClass } from '@/api/schemas/dashboard';
import { DashboardWidgetFrame, type DashboardWidgetProps } from './widget-frame';

/**
 * The platform's OPERATIONS CONTROL CENTRE — M63.
 *
 * ---------------------------------------------------------------------------
 * WHAT IT SHOWS, AND WHAT IT CANNOT
 * ---------------------------------------------------------------------------
 * M63 keeps the interventions the platform has opened against tenants, and the approvals they
 * are waiting on. This widget shows the four queue depths — how many interventions await
 * authorisation, how many await an outcome, how many approvals are open and how many are
 * approaching expiry. The tenant's own slice of that record is a SEPARATE widget
 * (`vendor-operations.tsx`), because one widget holds one read: a second read that failed would
 * otherwise take the queue counts down with it.
 *
 * The queues are counted over M63's OWN records. They are not a count of every problem in the
 * deployment: the domains the platform has not mounted (bookings, payments, subscriptions,
 * marketing, websites) contribute nothing, and the widget does not imply that they do. The
 * platform's own setup projection is where those are named, with the reason each is absent.
 *
 * ---------------------------------------------------------------------------
 * A PLATFORM-SCOPED READ
 * ---------------------------------------------------------------------------
 * The backlog needs the `operations.backlog.read` platform grant. The dashboard renders this
 * widget only for an actor who holds it; the frame still renders the shared refused state if
 * the server says no.
 */
const QUEUE_LABEL: Record<BacklogQueueClass, string> = {
  admin_action_pending_authorization: 'Awaiting authorisation',
  admin_action_outcome_unconfirmed: 'Awaiting an outcome',
  approval_case_pending: 'Approvals open',
  approval_case_expiring: 'Approvals expiring',
};

const QUEUE_ORDER: ReadonlyArray<BacklogQueueClass> = [
  'admin_action_pending_authorization',
  'admin_action_outcome_unconfirmed',
  'approval_case_pending',
  'approval_case_expiring',
];

export function OperationsControlCentre({ className }: DashboardWidgetProps): ReactNode {
  const load = useCallback(() => dashboardApi.operationsBacklog(), []);

  return (
    <DashboardWidgetFrame
      title="Operations control centre"
      load={load}
      className={className}
      isEmpty={(data) => data.queues.length === 0}
      emptyTitle="No queues"
      emptyDescription="The platform reported no operation queues."
    >
      {(data) => {
        const counts = new Map(data.queues.map((queue) => [queue.queueClass, queue]));

        // The API states the instant it counted at, in ISO. An ISO string on a card is a
        // timestamp no operator reads at a glance, so it is rendered the way the rest of the
        // console renders a time: the day and the wall clock, in the viewer's zone.
        const countedAt = `${formatDate(data.asOf)}, ${formatTime(data.asOf)}`;

        return (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {QUEUE_ORDER.map((queueClass) => {
              const count = counts.get(queueClass)?.openCount ?? 0;
              return (
                <StatCard
                  key={queueClass}
                  label={QUEUE_LABEL[queueClass]}
                  value={String(count)}
                  tone={count === 0 ? 'neutral' : 'warning'}
                  caption={`Counted at ${countedAt}`}
                />
              );
            })}
          </div>
        );
      }}
    </DashboardWidgetFrame>
  );
}
